import { useCallback, useEffect } from 'react'
import { getMutationErrorMessage } from '@/lib/api/mutation-feedback'
import { createLogger } from '@/lib/logger'
/**
 * Clear App Data Hook
 * Listens for clear-app-data event from the Tauri backend menu
 * Shows a confirmation dialog before clearing all app data
 */

import { clearAppData, isTauri } from '@/lib/utils/tauri'
import { toast } from 'sonner'

const logger = createLogger('hooks/useClearAppData')

export function useClearAppData(): void {
  const handleClearData = useCallback(async () => {
    if (!isTauri()) return
    try {
      // Show confirmation dialog using Tauri's dialog plugin
      const { confirm } = await import('@tauri-apps/plugin-dialog')

      const confirmed = await confirm(
        'This clears cached data and cookies and signs you out. Scrambl reloads when it’s done.',
        {
          title: 'Clear app data',
          kind: 'warning',
          okLabel: 'Clear data',
          cancelLabel: 'Cancel',
        },
      )

      if (confirmed) {
        toast.loading('Clearing app data…', { id: 'clear-data' })

        // Clear all app data
        await clearAppData()

        toast.success('App data cleared', {
          id: 'clear-data',
          description: 'Reloading app…',
        })

        // Give user a moment to see the message, then reload
        setTimeout(() => {
          window.location.href = '/login'
        }, 1000)
      }
    } catch (error) {
      logger.error('Failed to clear app data:', error)
      toast.error(
        getMutationErrorMessage(error, "Couldn't clear the app data."),
        {
          id: 'clear-data',
          description: error instanceof Error ? error.message : 'Unknown error',
        },
      )
    }
  }, [])

  // Listen for clear-app-data event from Tauri backend
  useEffect(() => {
    if (!isTauri()) return

    let unlisten: (() => void) | undefined

    const setupListener = async () => {
      try {
        const { listen } = await import('@tauri-apps/api/event')

        unlisten = await listen('clear-app-data', () => {
          handleClearData()
        })
      } catch (error) {
        logger.error('Failed to set up clear-app-data listener:', error)
      }
    }

    setupListener()

    return () => {
      if (unlisten) {
        unlisten()
      }
    }
  }, [handleClearData])
}

export default useClearAppData
